/**
 * CommentForm component
 *
 * A form with a textarea, comment type selector, and submit button.
 * Used both inline in the Comments tab and inside the AddCommentDialog.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send } from "lucide-react";
import type { CommentType } from "@/types/issueComment";
import { useCreateIssueComment } from "@/hooks/useIssueComments";

const COMMENT_TYPE_OPTIONS: { value: CommentType; label: string }[] = [
  { value: "GENERAL", label: "General" },
  { value: "INVESTIGATION_NOTE", label: "Investigation Note" },
  { value: "DECISION_RATIONALE", label: "Decision Rationale" },
  { value: "FALSE_POSITIVE_JUSTIFICATION", label: "False Positive Justification" },
];

interface CommentFormProps {
  issueId: number;
  onSuccess?: () => void;
}

export function CommentForm({ issueId, onSuccess }: CommentFormProps) {
  const [content, setContent] = useState("");
  const [commentType, setCommentType] = useState<CommentType>("GENERAL");

  const createMutation = useCreateIssueComment();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    createMutation.mutate(
      {
        issueId,
        data: {
          content: content.trim(),
          comment_type: commentType,
        },
      },
      {
        onSuccess: () => {
          setContent("");
          setCommentType("GENERAL");
          onSuccess?.();
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Textarea
        placeholder="Write a comment..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="min-h-[80px] text-sm"
        maxLength={10000}
        disabled={createMutation.isPending}
      />

      <div className="flex items-center justify-between gap-3">
        <Select
          value={commentType}
          onValueChange={(value) => setCommentType(value as CommentType)}
          disabled={createMutation.isPending}
        >
          <SelectTrigger className="w-[220px] h-8 text-xs">
            <SelectValue placeholder="Comment type" />
          </SelectTrigger>
          <SelectContent>
            {COMMENT_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          type="submit"
          size="sm"
          className="gap-1.5"
          disabled={createMutation.isPending || !content.trim()}
        >
          <Send className="h-3.5 w-3.5" />
          {createMutation.isPending ? "Posting..." : "Post Comment"}
        </Button>
      </div>
    </form>
  );
}
